import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import './ProductTypes.css';
import { API_CONFIG, getApiUrl, apiFetch } from '../../config/api';

const ProductTypesList = forwardRef(({ onSelectProductType, selectedProductTypeId, onNew }, ref) => {
  const { t } = useTranslation('production');
  const [productTypes, setProductTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');

  useImperativeHandle(ref, () => ({
    fetchProductTypes
  }));

  useEffect(() => {
    fetchProductTypes();
  }, [statusFilter]);

  const fetchProductTypes = async () => {
    setLoading(true);
    try {
      const response = await apiFetch(getApiUrl('/api/product-types/'));
      if (response.ok) {
        const data = await response.json();
        setProductTypes(data.product_types || []);
      } else {
        setError(t('productTypes.list.error.fetchFailed'));
      }
    } catch (error) {
      console.error('Error fetching product types:', error);
      setError(t('productTypes.list.error.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const filteredProductTypes = productTypes.filter(productType => {
    const matchesSearch = (productType.type_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          productType.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          productType.workflow_name?.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus = statusFilter === 'all' ||
                         (statusFilter === 'active' && productType.is_active) ||
                         (statusFilter === 'inactive' && !productType.is_active);

    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (isActive) => {
    return (
      <span className={`status-badge ${isActive ? 'status-active' : 'status-inactive'}`}>
{isActive ? t('productTypes.list.status.active') : t('productTypes.list.status.inactive')}
      </span>
    );
  };

  const handleNewClick = () => {
    if (onNew) {
      onNew();
    }
  };

  if (loading && productTypes.length === 0) {
    return <div className="list-container"><div className="loading">{t('productTypes.list.loading')}</div></div>;
  }

  return (
    <div className="list-container">
      <div className="list-header">
        <h2>{t('productTypes.list.title')}</h2>
        <div className="header-actions">
          <button className="btn-primary" onClick={handleNewClick}>
            <i className="icon">+</i> {t('productTypes.list.newProductType')}
          </button>
        </div>
      </div>

      <div className="list-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder={t('productTypes.list.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t('productTypes.list.filters.allStatus')}</option>
            <option value="active">{t('productTypes.list.filters.active')}</option>
            <option value="inactive">{t('productTypes.list.filters.inactive')}</option>
          </select>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="list-table">
        <table>
          <thead>
            <tr>
              <th>{t('productTypes.list.headers.typeName')}</th>
              <th>{t('productTypes.list.headers.description')}</th>
              <th>{t('productTypes.list.headers.workflow')}</th>
              <th>{t('productTypes.list.headers.status')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredProductTypes.length === 0 ? (
              <tr>
                <td colSpan="4" className="no-data">
                  {searchTerm ? t('productTypes.list.noData.noResults') : t('productTypes.list.noData.noProductTypes')}
                </td>
              </tr>
            ) : (
              filteredProductTypes.map(productType => (
                <tr
                  key={productType.product_type_id}
                  onClick={() => onSelectProductType(productType.product_type_id)}
                  className={selectedProductTypeId === productType.product_type_id ? 'selected' : ''}
                >
                  <td className="product-type-info">
                    <div className="product-type-name">
                      <strong>{productType.type_name || t('productTypes.list.defaultValues.unknownType')}</strong>
                    </div>
                  </td>
                  <td className="product-type-description">
{productType.description || t('productTypes.list.defaultValues.notAvailable')}
                  </td>
                  <td className="process-template-info">
                    <div className="template-name">
                      <strong>{productType.workflow_name || t('productTypes.list.defaultValues.noWorkflow')}</strong>
                    </div>
                    <div className="template-duration">
{productType.workflow_description || t('productTypes.list.defaultValues.notAvailable')}
                    </div>
                  </td>
                  <td>
                    {getStatusBadge(productType.is_active)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="list-footer">
        <div className="record-count">
{t('productTypes.list.footer.recordCount', { filtered: filteredProductTypes.length, total: productTypes.length })}
        </div>
      </div>
    </div>
  );
});

ProductTypesList.displayName = 'ProductTypesList';

export default ProductTypesList;